import { Card, Skeleton, SkeletonAvatar, SkeletonButton, SkeletonRegion } from '@parsina/framework';
import { C, RADIUS, soft } from '@parsina/framework/tokens';
import { useTranslation } from '../../i18n';
import useResponsive from '../../hooks/useResponsive';
import { SETTINGS_SECTIONS, resolveSettingsSection, settingsSectionLabel } from './Settings.shared';
import SettingsDesktopOnly from './SettingsDesktopOnly';

/**
 * «تنظیمات» while its chunk loads — the section rail (real, its labels are
 * static) and the ACTIVE section's own shape: the profile's avatar card, the
 * language card, the WhatsApp link. A generic form would be a blank with a
 * title; each section here reserves the geometry it will fill.
 *
 * The router's Suspense fallback for `/settings` (it reads `?section=` through
 * the shared resolver, so the fallback draws the section the URL names).
 *
 * The workstation is all of it. The phone surface was removed — «تنظیمات» is
 * desktop-only there and says so in one note — so the mobile geometry that used
 * to live here went with it, and the mobile branch below is that same note.
 *
 * `Settings.shared.js` is the one non-kit import: it holds no React and no data,
 * only the registry the surface already reads.
 */

// ── Desktop pieces (the geometry of Settings.web's own cards) ──────────────────

const CARD = { padding: 24, marginBottom: 20 };

/** `SectionTitle`: the real h2 + a hint line — static copy, rendered for real. */
function TitleWeb({ title, sub }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 800, color: C.textPrimary }}>{title}</h2>
      {sub ? <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', color: C.textMuted }}>{sub}</p> : null}
    </div>
  );
}

/** A card's bold 14px title line. */
function CardTitle({ width = 140, marginBottom = 16 }) {
  return <Skeleton width={width} height={16} style={{ marginBottom }} />;
}

function ProfileWeb() {
  return (
    <>
      <Card aria-hidden style={{ padding: 24, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 20 }}>
        <SkeletonAvatar size={72} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <Skeleton width={160} height={17} style={{ marginBottom: 12 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <Skeleton width={104} height={34} radius={8} />
            <Skeleton width={72} height={34} radius={8} />
          </div>
        </div>
      </Card>
    </>
  );
}

function AppearanceWeb() {
  return (
    <Card aria-hidden style={{ padding: 24, marginBottom: 16 }}>
      <CardTitle marginBottom={4} />
      <Skeleton width="55%" height={13} style={{ marginBottom: 14 }} />
      <Skeleton width={200} height={42} radius={RADIUS.control} />
    </Card>
  );
}

function WhatsappWeb() {
  return (
    <Card aria-hidden style={CARD}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
        <Skeleton width={44} height={44} radius={12} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Skeleton width="40%" height={15} />
          <Skeleton width="65%" height={12} />
        </div>
        <Skeleton width={72} height={24} radius={RADIUS.pill} />
      </div>
      <SkeletonButton width={160} />
    </Card>
  );
}

const WEB_SECTIONS = {
  profile: ProfileWeb,
  appearance: AppearanceWeb,
  whatsapp: WhatsappWeb,
};

/** The hint key each section prints under its title (static copy). */
const HINT_KEYS = {
  profile: 'settings.profile.hint',
  appearance: 'settings.appearance.hint',
};

/** One desktop section's body, in placeholder — what Settings.web renders where the section will be. */
export function SettingsSectionSkeletonWeb({ section }) {
  const { t } = useTranslation();
  const id = resolveSettingsSection(section);
  const Body = WEB_SECTIONS[id] || ProfileWeb;
  const hintKey = HINT_KEYS[id];
  return (
    <SkeletonRegion
      label={t('common.state.loading')}
      data-testid="settings-skeleton-body" data-section={id}>
      <TitleWeb title={settingsSectionLabel(id, t)} sub={hintKey ? t(hintKey) : null} />
      <Body />
    </SkeletonRegion>
  );
}

/** The desktop page: the section rail for real, the active section in placeholder. */
export function SettingsSkeletonWeb({ section }) {
  const { t } = useTranslation();
  const active = resolveSettingsSection(section);
  return (
    <div data-state="loading" data-testid="settings-skeleton"
      style={{ height: '100%', display: 'flex', overflow: 'hidden', background: '#f8fafc' }}>
      <div style={{
        width: 220, flexShrink: 0,
        background: 'white', borderInlineStart: `1px solid ${C.border}`,
        display: 'flex', flexDirection: 'column',
        padding: '24px 12px',
        overflowY: 'auto',
      }}>
        <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: C.textMuted, marginBottom: 10, padding: '0 8px', letterSpacing: 0.8 }}>
          {t('settings.title')}
        </div>
        {SETTINGS_SECTIONS(t).map((s) => (
          <div key={s.id} aria-current={active === s.id ? 'page' : undefined} style={{
            width: '100%', padding: '11px 14px',
            display: 'flex', alignItems: 'center', gap: 10,
            background: active === s.id ? soft(C.navy, 0.1) : 'transparent',
            borderRadius: 10,
            color: active === s.id ? C.navy : C.textSecondary,
            fontSize: '0.8125rem', fontWeight: active === s.id ? 700 : 500,
            textAlign: 'end',
          }}>
            <span style={{ fontSize: '1rem' }}>{s.icon}</span>
            {s.label}
          </div>
        ))}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '36px 40px' }}>
        <SettingsSectionSkeletonWeb section={active} />
      </div>
    </div>
  );
}

export default function SettingsSkeleton(props) {
  const { isMobile } = useResponsive();
  // The phone has nothing to wait for: «تنظیمات» is desktop-only there and its
  // surface is one note. Rendering that as the fallback too means the lazy
  // chunk lands on an identical screen — and, more to the point, the desktop
  // rail never flashes at 360px on the way past.
  return isMobile ? <SettingsDesktopOnly /> : <SettingsSkeletonWeb {...props} />;
}
